<template>
  <footer class="app-footer">
    <div class="app-footer__blocks">
      <div class="app-footer__block">
        <TextLogo />
        <div
          v-if="!simple"
          class="app-footer__contact-options"
        >
          <a
            :href="whatsAppContactLink"
            class="app-footer__contact-option"
            target="_blank"
            aria-label="WhatsApp"
          >
            <Icon name="ic:baseline-whatsapp" />
          </a>
          <a
            :href="emailContactLink"
            class="app-footer__contact-option"
            target="_blank"
            aria-label="E-mail"
          >
            <Icon name="ic:outline-email" />
          </a>
          <a
            href="https://www.facebook.com/people/Momentshareio/61550916671583/"
            class="app-footer__contact-option"
            target="_blank"
            aria-label="Facebook"
          >
            <Icon name="ic:baseline-facebook" />
          </a>
          <a
            href="https://www.instagram.com/momentshare_io/"
            class="app-footer__contact-option"
            target="_blank"
            aria-label="Instagram"
          >
            <Icon name="ph:instagram-logo" />
          </a>
        </div>
        <LanguageSwitch
          native
          class="app-footer__language-switch"
        />
        <a
          href="https://websitevhjaar.nl"
          target="_blank"
          class="app-footer__woty-image"
        >
          <NuxtImg
            v-if="!simple"
            :src="wotyImageSrc"
            alt="Website of the year"
            loading="lazy"
            width="146"
            height="146"
          />
        </a>
      </div>
      <div
        v-if="!simple"
        class="app-footer__block"
      >
        <span class="app-footer__block-title">{{ $t('useful_links') }}</span>
        <div class="app-footer__block-links">
          <NuxtLink
            class="app-footer__link"
            :to="aboutLink"
          >
            {{ $t('about_link_text') }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link"
            :to="helpLink"
          >
            {{ $t('help_link_text') }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link"
            :to="reviewsLink"
          >
            {{ $t('reviews') }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link"
            :to="termAndConditionsLink"
          >
            {{ $t('terms_and_conditions') }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link"
            :to="privacyPolicyLink"
          >
            {{ $t('privacy_policy') }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link"
            :to="blogLink"
          >
            {{ $t('blog_link_text') }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link"
            :to="contactLink"
          >
            {{ $t('contact') }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link"
            :to="accountLink"
          >
            {{ $t('my_account') }}
          </NuxtLink>
          <ClientOnly>
            <button
              v-if="isAuthenticated"
              class="app-footer__link"
              @click="logout"
            >
              {{ $t('logout') }}
            </button>
          </ClientOnly>
        </div>
      </div>
      <div
        v-if="!simple"
        class="app-footer__block"
      >
        <span class="app-footer__block-title">{{ $t('perfect_for') }}</span>
        <div class="app-footer__block-links">
          <NuxtLink
            class="app-footer__link"
            :to="weddingContentPageLink"
          >
            {{ $t('wedding_link_text') }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link"
            :to="weddingAnniversaryContentPageLink"
          >
            {{ $t('wedding_anniversary_link_text') }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link"
            :to="companyPartyContentPageLink"
          >
            {{ $t('company_outing_link_text') }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link"
            :to="birthdayContentPageLink"
          >
            {{ $t('birthday_link_text') }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link"
            :to="sportsEventsContentPageLink"
          >
            {{ $t('sport_event_link_text') }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link"
            :to="vacationContentPageLink"
          >
            {{ $t('vacation_link_text') }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link"
            :to="christmasContentPageLink"
          >
            {{ $t('christmas_link_text') }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link"
            :to="newYearsEveContentPageLink"
          >
            {{ $t('end_of_year_link_text') }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link"
            :to="familyGatheringsContentPageLink"
          >
            {{ $t('family_gathering_link_text') }}
          </NuxtLink>
        </div>
      </div>
      <div
        v-if="!simple"
        class="app-footer__block app-footer__block--blog"
      >
        <span class="app-footer__block-title">{{ $t('blog_link_text') }}</span>
        <div class="app-footer__block-links">
          <NuxtLink
            v-for="post in latestBlogPosts"
            :key="post.id"
            class="app-footer__link"
            :to="localePath(`/blog/${$t(post.slug)}`)"
          >
            {{ $t(post.titleShort) }}
          </NuxtLink>
          <NuxtLink
            class="app-footer__link app-footer__link--all-posts"
            :to="blogLink"
          >
            {{ $t('blog.footer.all_posts') }}
          </NuxtLink>
        </div>
      </div>
      <div
        v-if="!simple"
        class="app-footer__block"
      >
        <span class="app-footer__block-title">{{ $t('also_available_as_app') }}</span>
        <p class="app-footer__app-tagline">{{ $t('also_available_as_app_tagline') }}</p>
        <div class="app-footer__app-badges">
          <a
            :href="appStoreLink"
            target="_blank"
            rel="noopener"
            class="app-footer__app-badge"
            :aria-label="$t('app_store_badge_alt')"
          >
            <img
              :src="appStoreBadge"
              :alt="$t('app_store_badge_alt')"
              height="40"
              width="119.66407"
            />
          </a>
          <a
            :href="playStoreLink"
            target="_blank"
            rel="noopener"
            class="app-footer__app-badge"
            :aria-label="$t('play_store_badge_alt')"
          >
            <img
              :src="playStoreBadge"
              :alt="$t('play_store_badge_alt')"
              width="300"
              height="89"
            />
          </a>
        </div>
      </div>
    </div>
  </footer>
</template>

<script setup>
  import { EMAIL_CONTACT_LINK, WHATSAPP_CONTACT_LINK } from '~/lib/constants/contact-options'
  import { APP_STORE_LINK, PLAY_STORE_LINK } from '~/lib/constants/store-links'
  import { blogPostsData } from '~/lib/blog-posts'
  import { useUserStore } from '~/stores/user'
  import { storeToRefs } from 'pinia'

  const FOOTER_BLOG_POSTS_LIMIT = 8

  defineProps({
    simple: {
      type: Boolean,
      default: false,
    },
  })

  const { t: $t, locale } = useI18n()
  const userStore = useUserStore()
  const { isAuthenticated } = storeToRefs(userStore)
  const {
    goToHomepage,
    weddingContentPageLink,
    birthdayContentPageLink,
    weddingAnniversaryContentPageLink,
    companyPartyContentPageLink,
    christmasContentPageLink,
    newYearsEveContentPageLink,
    sportsEventsContentPageLink,
    vacationContentPageLink,
    familyGatheringsContentPageLink,
    termAndConditionsLink,
    privacyPolicyLink,
    accountLink,
    helpLink,
    aboutLink,
    reviewsLink,
    contactLink,
    blogLink,
  } = useLinks()
  const localePath = useLocalePath()
  const userLogoutService = useUserLogoutService()

  // The registry is maintained newest-first, same order the blog overview renders.
  const latestBlogPosts = blogPostsData.slice(0, FOOTER_BLOG_POSTS_LIMIT)

  const whatsAppContactLink = WHATSAPP_CONTACT_LINK
  const emailContactLink = EMAIL_CONTACT_LINK
  const appStoreLink = APP_STORE_LINK
  const playStoreLink = PLAY_STORE_LINK

  // Per-language store badges live in /public/badges/. The unsuffixed
  // SVGs are the English ones (Apple/Google's universal fallback); the
  // -<locale> variants ship the official translated badges per
  // Apple/Google brand guidelines. Locale codes that don't have a
  // dedicated badge file fall back to the English SVG.
  const BADGE_LOCALES = new Set(['nl', 'de', 'fr', 'es'])
  const badgeSuffix = computed(() => (BADGE_LOCALES.has(locale.value) ? `-${locale.value}` : ''))
  const appStoreBadge = computed(() => `/badges/app-store-badge${badgeSuffix.value}.svg`)
  const playStoreBadge = computed(() => `/badges/google-play-badge${badgeSuffix.value}.svg`)

  const wotyImageSrc = computed(() => (locale.value === 'nl' ? `/logos/woty-${locale.value}.svg` : '/logos/woty.svg'))

  async function logout() {
    await goToHomepage()

    userLogoutService.logout()
  }
</script>

<style lang="scss">
  .app-footer {
    margin: 0 auto;
    width: 100%;
    display: flex;
    background: var(--color-primary);
    flex-direction: column;
  }

  .app-footer__blocks {
    max-width: var(--max-width-content);
    padding: 0 var(--content-padding) 48px;
    width: 100%;
    margin: 0 auto;
    display: flex;
    justify-content: space-between;
    flex-direction: column;
    gap: 32px;

    @include breakpoint(desktop) {
      flex-direction: row;
    }
  }

  .app-footer__block {
    display: flex;
    flex-direction: column;
    gap: 16px;
    color: var(--color-lightest);
  }

  .app-footer__block--blog {
    @include breakpoint(desktop) {
      max-width: 260px;
    }
  }

  .app-footer__block-title {
    font-weight: 700;
    font-size: 16px;
    line-height: 23px;

    @include breakpoint(desktop) {
      margin: 12px 0 0;
    }
  }

  .app-footer__language-switch {
    align-self: flex-start;

    @include breakpoint(desktop) {
      align-self: stretch;
    }
  }

  .app-footer__block-links {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .app-footer__link {
    font-weight: 400;
    font-size: 16px;
    transition: color 0.2s ease-in-out;
  }

  .app-footer__link:hover {
    color: var(--color-secondary);
  }

  .app-footer__contact-options {
    display: flex;
    gap: var(--spacing-default);
  }

  .app-footer__contact-option {
    font-size: 2rem;
    color: var(--color-secondary);
    display: flex;
  }

  .app-footer__woty-image {
    margin: 16px 0 0;
  }

  .app-footer__app-tagline {
    color: rgba(255, 255, 255, 0.7);
    font-size: 0.9rem;
    line-height: 1.4;
    margin: 0 0 12px;
    max-width: 240px;
  }

  .app-footer__app-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }

  .app-footer__app-badge {
    display: inline-block;
  }

  .app-footer__app-badge img {
    display: block;
    height: 40px;
    width: auto;
  }
</style>
